/**
 * Suporte (dono, 2026-10-17): lista dos tickets do jogador + "Abrir ticket" + conversa, com IMAGEM anexada.
 * O mesmo sistema de ticket do jogo (NextGuard) e do painel GM: /tickets, /tickets/{id}, /tickets/{id}/messages.
 * Imagem: depois de criar a mensagem, cada arquivo sobe sozinho em /tickets/{id}/messages/{msg}/attachments
 * (fica no disco do host da API; o cron apaga quando o ticket fecha). Ate' TICKET_IMG_MAX por mensagem, 5 MB cada.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { ArrowLeft, ImagePlus, Loader2, MessageSquarePlus, RefreshCw, Send, X } from 'lucide-react';
import {
  TICKET_IMG_MAX,
  TICKET_IMG_MAX_BYTES,
  createTicket,
  getTicket,
  listMyTickets,
  replyTicket,
  ticketImgSrc,
  uploadTicketImage,
  type TicketDetail,
  type TicketSummary,
} from '@/shared/services/ticketsService';
import { NxModal, errMsg, fmtDateTime } from './NxModal';

const STATUS: Record<string, { label: string; tone: string }> = {
  open: { label: 'Aberto', tone: 'open' },
  answered: { label: 'Respondido', tone: 'answered' },
  closed: { label: 'Fechado', tone: 'closed' },
};
const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

type View = { kind: 'list' } | { kind: 'new' } | { kind: 'detail'; ticket: TicketDetail; notice?: string };

function StatusChip({ status }: { status: string }) {
  const s = STATUS[status] || STATUS.open;
  return <span className={`nxd-chip nxd-chip--${s.tone}`}>{s.label}</span>;
}

/** Escolha das imagens (com previa). Confere tipo/tamanho/quantidade antes de mandar. */
function ImagePicker({ files, onChange, onError }: { files: File[]; onChange: (f: File[]) => void; onError: (m: string) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [previews, setPreviews] = useState<string[]>([]);

  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  const add = (list: FileList | null) => {
    if (!list) return;
    const next = [...files];
    for (const f of Array.from(list)) {
      if (!ACCEPT.split(',').includes(f.type)) {
        onError(`"${f.name}" não é PNG, JPG, WEBP ou GIF.`);
        continue;
      }
      if (f.size > TICKET_IMG_MAX_BYTES) {
        onError(`"${f.name}" passa de 5 MB.`);
        continue;
      }
      if (next.length >= TICKET_IMG_MAX) {
        onError(`No máximo ${TICKET_IMG_MAX} imagens por mensagem.`);
        break;
      }
      next.push(f);
    }
    onChange(next);
    if (input.current) input.current.value = '';
  };

  return (
    <div className="nxd-pick">
      {previews.map((src, i) => (
        <figure key={src} className="nxd-pick__item">
          <img src={src} alt="" />
          <button type="button" aria-label="Remover imagem" onClick={() => onChange(files.filter((_, j) => j !== i))}>
            <X size={14} />
          </button>
        </figure>
      ))}
      {files.length < TICKET_IMG_MAX && (
        <button type="button" className="nxd-pick__add" onClick={() => input.current?.click()}>
          <ImagePlus size={20} />
          <span>Anexar imagem</span>
          <small>até {TICKET_IMG_MAX}, 5 MB cada</small>
        </button>
      )}
      <input ref={input} type="file" accept={ACCEPT} multiple hidden onChange={(e) => add(e.target.files)} />
    </div>
  );
}

/** Sobe as imagens na ultima mensagem do jogador; devolve o ticket atualizado e quantas falharam. */
async function sendImages(ticket: TicketDetail, files: File[]): Promise<{ ticket: TicketDetail; failed: string[] }> {
  const mine = [...ticket.messages].reverse().find((m) => !m.isStaff);
  if (!mine || files.length === 0) return { ticket, failed: [] };
  let current = ticket;
  const failed: string[] = [];
  for (const f of files) {
    try {
      current = await uploadTicketImage(ticket.id, mine.id, f);
    } catch (e: any) {
      failed.push(`${f.name}: ${errMsg(e, 'falhou')}`);
    }
  }
  return { ticket: current, failed };
}

export default function NextSupportPage() {
  const [view, setView] = useState<View>({ kind: 'list' });
  const [tickets, setTickets] = useState<TicketSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState<number | null>(null);

  const loadList = useCallback(async () => {
    setError(null);
    try {
      setTickets(await listMyTickets());
    } catch (e: any) {
      setTickets([]);
      setError(errMsg(e, 'Não foi possível carregar seus tickets.'));
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const open = async (id: number) => {
    setOpening(id);
    setError(null);
    try {
      setView({ kind: 'detail', ticket: await getTicket(id) });
    } catch (e: any) {
      setError(errMsg(e, 'Não foi possível abrir o ticket.'));
    } finally {
      setOpening(null);
    }
  };

  const backToList = () => {
    setView({ kind: 'list' });
    void loadList();
  };

  if (view.kind === 'new')
    return <NewTicket onCancel={backToList} onCreated={(ticket, notice) => setView({ kind: 'detail', ticket, notice })} />;
  if (view.kind === 'detail')
    return (
      <TicketThread
        key={view.ticket.id}
        ticket={view.ticket}
        notice={view.notice}
        onBack={backToList}
        onChange={(ticket) => setView({ kind: 'detail', ticket })}
      />
    );

  return (
    <div className="nxd-stack">
      <header className="nxd-heading nxd-heading--row">
        <div>
          <p className="nx-eyebrow">Painel do jogador</p>
          <h1>Suporte</h1>
        </div>
        <div className="nxd-heading__actions">
          <button type="button" className="nxd-btn nxd-btn--icon" onClick={() => void loadList()} aria-label="Atualizar">
            <RefreshCw size={15} />
          </button>
          <button type="button" className="nxd-btn nxd-btn--primary" onClick={() => setView({ kind: 'new' })}>
            <MessageSquarePlus size={16} /> Abrir ticket
          </button>
        </div>
      </header>

      {error && <p className="nxd-notice nxd-notice--error">{error}</p>}

      {tickets === null ? (
        <div className="nxd-loading">
          <Loader2 size={26} className="nxb-spin" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="nxd-card nxd-empty">
          Você ainda não abriu nenhum ticket. Precisa de ajuda? Clique em <b>Abrir ticket</b>.
        </div>
      ) : (
        <ul className="nxd-tickets">
          {tickets.map((t) => (
            <li key={t.id}>
              <button type="button" className="nxd-ticket" onClick={() => void open(t.id)} disabled={opening !== null}>
                <span className="nxd-ticket__id">#{t.id}</span>
                <span className="nxd-ticket__subject">{t.subject}</span>
                {t.status === 'answered' && t.lastReplyByStaff && <span className="nxd-ticket__new">Nova resposta</span>}
                <StatusChip status={t.status} />
                <span className="nxd-ticket__date">{fmtDateTime(t.updatedAt)}</span>
                {opening === t.id && <Loader2 size={16} className="nxb-spin" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewTicket({ onCancel, onCreated }: { onCancel: () => void; onCreated: (t: TicketDetail, notice?: string) => void }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const s = subject.trim();
    const b = body.trim();
    if (s.length < 3) return setError('Assunto: mínimo 3 caracteres.');
    if (b.length < 3) return setError('Mensagem: mínimo 3 caracteres.');
    setLoading(true);
    setError(null);
    try {
      const created = await createTicket({ subject: s, body: b });
      const { ticket, failed } = await sendImages(created, files);
      onCreated(ticket, failed.length ? `Ticket aberto, mas estas imagens não foram: ${failed.join('; ')}` : undefined);
    } catch (err: any) {
      setError(errMsg(err, 'Não foi possível abrir o ticket.'));
      setLoading(false);
    }
  };

  return (
    <div className="nxd-stack">
      <header className="nxd-heading">
        <button type="button" className="nxd-back" onClick={onCancel} disabled={loading}>
          <ArrowLeft size={16} /> Meus tickets
        </button>
        <h1>Abrir ticket</h1>
      </header>
      <form className="nxd-card nxd-form" onSubmit={submit} noValidate>
        <label className="nxb-field">
          <span>Assunto</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120} placeholder="Resumo do problema" />
        </label>
        <label className="nxb-field">
          <span>Mensagem</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={4000}
            rows={7}
            placeholder="Conte o que aconteceu: personagem, canal, horário..."
          />
          <small>{body.length}/4000</small>
        </label>
        <ImagePicker files={files} onChange={setFiles} onError={setError} />
        {error && <p className="nxb-error">{error}</p>}
        <div className="nxd-form__actions">
          <button type="button" className="nxd-btn" onClick={onCancel} disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="nxd-btn nxd-btn--primary" disabled={loading}>
            {loading ? <Loader2 size={16} className="nxb-spin" /> : <Send size={15} />}
            {loading ? 'Enviando...' : 'Enviar ticket'}
          </button>
        </div>
      </form>
    </div>
  );
}

type ThreadProps = { ticket: TicketDetail; notice?: string; onBack: () => void; onChange: (t: TicketDetail) => void };

function TicketThread({ ticket, notice, onBack, onChange }: ThreadProps) {
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(notice ?? null);
  const [zoom, setZoom] = useState<string | null>(null);
  const closed = ticket.status === 'closed';

  const reply = async (e: FormEvent) => {
    e.preventDefault();
    const b = body.trim();
    if (!b) return setError('Escreva a sua resposta.');
    setLoading(true);
    setError(null);
    try {
      const updated = await replyTicket(ticket.id, { body: b });
      const { ticket: withImages, failed } = await sendImages(updated, files);
      setBody('');
      setFiles([]);
      onChange(withImages);
      if (failed.length) setError(`Resposta enviada, mas estas imagens não foram: ${failed.join('; ')}`);
    } catch (err: any) {
      setError(errMsg(err, 'Não foi possível enviar a resposta.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="nxd-stack">
      <header className="nxd-heading">
        <button type="button" className="nxd-back" onClick={onBack}>
          <ArrowLeft size={16} /> Meus tickets
        </button>
        <h1 className="nxd-thread__title">
          <span>#{ticket.id}</span> {ticket.subject}
        </h1>
        <p className="nxd-thread__meta">
          <StatusChip status={ticket.status} /> Aberto em {fmtDateTime(ticket.createdAt)}
        </p>
      </header>

      <ol className="nxd-thread">
        {ticket.messages.map((m) => (
          <li key={m.id} className={`nxd-msg ${m.isStaff ? 'nxd-msg--staff' : 'nxd-msg--mine'}`}>
            <div className="nxd-msg__head">
              <b>{m.isStaff ? 'Equipe Next' : 'Você'}</b>
              <span>{fmtDateTime(m.createdAt)}</span>
            </div>
            <p className="nxd-msg__body">{m.body}</p>
            {!!m.attachments?.length && (
              <div className="nxd-msg__imgs">
                {m.attachments.map((url) => (
                  <button key={url} type="button" onClick={() => setZoom(ticketImgSrc(url))} aria-label="Ver imagem">
                    <img src={ticketImgSrc(url)} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ol>

      {closed ? (
        <p className="nxd-notice">Este ticket foi fechado. Se precisar de mais ajuda, abra um ticket novo.</p>
      ) : (
        <form className="nxd-card nxd-form" onSubmit={reply} noValidate>
          <label className="nxb-field">
            <span>Responder</span>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} rows={4} placeholder="Escreva sua resposta..." />
          </label>
          <ImagePicker files={files} onChange={setFiles} onError={setError} />
          {error && <p className="nxb-error">{error}</p>}
          <div className="nxd-form__actions">
            <button type="submit" className="nxd-btn nxd-btn--primary" disabled={loading}>
              {loading ? <Loader2 size={16} className="nxb-spin" /> : <Send size={15} />}
              {loading ? 'Enviando...' : 'Enviar resposta'}
            </button>
          </div>
        </form>
      )}

      <NxModal open={!!zoom} onClose={() => setZoom(null)} className="nxb-modal--image">
        {zoom && <img className="nxd-zoom" src={zoom} alt="" />}
      </NxModal>
    </div>
  );
}
